import { Map as ImmutableMap, Record as ImmutableRecord } from 'immutable';

import {
  MESSAGES_MESSAGE_RECEIVED,
  MESSAGES_THREAD_READ,
  MESSAGES_THREADS_FETCH_SUCCESS,
} from 'mastodon/actions/messages';
import type {
  MessageCreatePayload,
  MessageThread,
} from 'mastodon/actions/messages';

interface MessagesState {
  unreadCount: number;
  threadUnreadCounts: ImmutableMap<string, number>;
  lastReceived: MessageCreatePayload | null;
}

const MessagesStateRecord = ImmutableRecord<MessagesState>({
  unreadCount: 0,
  threadUnreadCounts: ImmutableMap<string, number>(),
  lastReceived: null,
});

type MessagesStateRecord = ReturnType<typeof MessagesStateRecord>;

type MessagesAction =
  | { type: typeof MESSAGES_THREADS_FETCH_SUCCESS; threads: MessageThread[] }
  | { type: typeof MESSAGES_MESSAGE_RECEIVED; payload: MessageCreatePayload }
  | { type: typeof MESSAGES_THREAD_READ; thread: MessageThread }
  | { type: string };

const unreadCountsFromThreads = (threads: MessageThread[]) =>
  ImmutableMap<string, number>(
    threads.map((thread) => [thread.id, thread.unread_count]),
  );

const totalUnread = (counts: ImmutableMap<string, number>) =>
  counts.reduce((sum, count) => sum + count, 0);

export const messagesReducer = (
  state: MessagesStateRecord = MessagesStateRecord(),
  action: MessagesAction,
) => {
  switch (action.type) {
    case MESSAGES_THREADS_FETCH_SUCCESS: {
      if (!('threads' in action)) return state;

      const counts = unreadCountsFromThreads(action.threads);
      return state.merge({
        threadUnreadCounts: counts,
        unreadCount: totalUnread(counts),
      });
    }
    case MESSAGES_MESSAGE_RECEIVED: {
      if (!('payload' in action)) return state;

      const counts = state.threadUnreadCounts.set(
        action.payload.thread.id,
        action.payload.thread.unread_count,
      );

      return state.merge({
        threadUnreadCounts: counts,
        unreadCount: action.payload.unread_count,
        lastReceived: action.payload,
      });
    }
    case MESSAGES_THREAD_READ: {
      if (!('thread' in action)) return state;

      const counts = state.threadUnreadCounts.set(
        action.thread.id,
        action.thread.unread_count,
      );

      return state.merge({
        threadUnreadCounts: counts,
        unreadCount: totalUnread(counts),
      });
    }
    default:
      return state;
  }
};
